import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Input, Form, Space, Tag, Switch } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  UserOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined,
  HomeOutlined, BankOutlined
} from '@ant-design/icons';
import BASEURL from '../API/Config';

const Vendor = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editVendorId, setEditVendorId] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/master/getvendor1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setVendors(data);
    } catch {
      Swal.fire('Error', 'Failed to load vendors.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const openModal = (vendor = null) => {
    if (vendor) {
      setIsEditing(true);
      setEditVendorId(vendor._id);
      form.setFieldsValue({ ...vendor });
    } else {
      setIsEditing(false);
      setEditVendorId(null);
      form.resetFields();
      form.setFieldsValue({ isActive: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      const url = isEditing
        ? `${BASEURL}/master/updatevendor/${editVendorId}`
        : `${BASEURL}/user/createvendor`;
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(values),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Success', `Vendor ${isEditing ? 'updated' : 'added'} successfully!`, 'success');
      setShowModal(false);
      fetchVendors();
    } catch (err) {
      Swal.fire('Error', err.message || 'Something went wrong.', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Vendor?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53935',
      confirmButtonText: 'Yes, Delete',
    });
    if (!result.isConfirmed) return;
    await fetch(`${BASEURL}/master/deletevendor/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    Swal.fire('Deleted!', 'Vendor removed successfully!', 'success');
    fetchVendors();
  };

  const filtered = vendors.filter(v =>
    `${v.firstname} ${v.lastname} ${v.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "S.No", key: "idx", width: 55,
      render: (_, __, i) => <span style={{ color: "#6b7280" }}>{i + 1}</span>,
    },
    {
      title: 'VENDOR', key: 'name',
      render: (r) => (
        <Space>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f57c00', fontWeight: 700, fontSize: 14 }}>
            {(r.firstname || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#212121', fontSize: 13 }}>{r.firstname} {r.lastname}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email}</div>
          </div>
        </Space>
      ),
    },
    { title: 'PHONE', dataIndex: 'phoneNo', key: 'phone', render: v => <span style={{ color: '#374151' }}>{v || '—'}</span> },
    { title: 'CITY', dataIndex: 'city', key: 'city', render: v => <span style={{ color: '#374151' }}>{v || '—'}</span> },
    { title: 'OFFICE', dataIndex: 'officeName', key: 'office', render: v => <span style={{ color: '#374151' }}>{v || '—'}</span> },
    {
      title: 'STATUS', key: 'status',
      render: (r) => r.isActive
        ? <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>
        : <Tag color="red" style={{ borderRadius: 50, fontWeight: 600 }}>Inactive</Tag>,
    },
    {
      title: 'ACTIONS', key: 'actions', width: 110,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  const inputFields = [
    { name: 'firstname', label: 'First Name', icon: <UserOutlined /> },
    { name: 'lastname', label: 'Last Name', icon: <UserOutlined /> },
    { name: 'email', label: 'Email', icon: <MailOutlined />, type: 'email' },
    { name: 'phoneNo', label: 'Phone', icon: <PhoneOutlined /> },
    { name: 'address', label: 'Address', icon: <HomeOutlined /> },
    { name: 'city', label: 'City', icon: <EnvironmentOutlined /> },
    { name: 'landmark', label: 'Landmark', icon: <EnvironmentOutlined /> },
    { name: 'state', label: 'State', icon: <EnvironmentOutlined /> },
    { name: 'pincode', label: 'Pincode', icon: <EnvironmentOutlined /> },
    { name: 'officeName', label: 'Office Name', icon: <BankOutlined /> },
    { name: 'officeAddress', label: 'Office Address', icon: <HomeOutlined /> },
    { name: 'officePhone', label: 'Office Phone', icon: <PhoneOutlined /> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Vendor Master</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Add Vendor
        </Button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search vendor..."
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280, borderRadius: 4 }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>
        <Table columns={columns} dataSource={filtered} rowKey="_id" loading={loading}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"], showTotal: (t) => `${t} vendors` }} size="middle" />
      </div>

      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Vendor' : 'Add Vendor'}</span>}
        open={showModal} onCancel={() => setShowModal(false)} footer={null} centered width={680}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {inputFields.map(({ name, label, icon, type }) => (
              <Form.Item key={name} name={name} label={label}
                rules={[{ required: true, message: `${label} is required` }]}>
                <Input prefix={icon} type={type || 'text'} size="large" placeholder={`Enter ${label.toLowerCase()}`} />
              </Form.Item>
            ))}
          </div>
          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive"
              style={{ background: '#2874f0' }} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update Vendor' : 'Add Vendor'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Vendor;
